import { section, p, list, steps, tip, warn, table, faq } from './blocks';

export default {
  slug: 'smarte-order-loesungen',
  title: 'Smarte Order-Lösungen',
  shortTitle: 'Smart Order',
  icon: 'QrCode',
  updated: 'Juni 2026',
  datePublished: '2026-06-24',
  dateModified: '2026-06-24',
  image: 'https://images.unsplash.com/photo-1609427955204-d0a737cb2c1a?w=1200&h=900&fit=crop',
  teaser: 'QR-Bestellung am Tisch, Self-Order-Kiosk, digitale Speisekarte und Küchendisplay: Welche Lösung zu welchem Konzept passt und wie du Service und Umsatz gleichzeitig steigerst.',
  mainArticle: {
    title: 'Smarte Order-Lösungen: QR-Bestellung, Kiosk und digitale Speisekarte',
    takeaways: [
      'QR-Bestellung, Kiosk und digitale Karte ergänzen den Service – sie ersetzen ihn nicht.',
      'Gut umgesetzt steigen Durchschnittsbons um 10 bis 30 Prozent, während das Team entlastet wird.',
      'Alle Kanäle müssen in dieselbe Kasse und denselben Artikelstamm laufen.',
      'Stabiles WLAN, kein App-Zwang und ein eingebundenes Team entscheiden über die Akzeptanz.',
    ],
    intro: 'Gäste bestellen heute anders: per QR-Code am Tisch, am Terminal im Eingangsbereich oder vorab auf dem Smartphone. Für Gastronomen bedeutet das weniger Laufwege, höhere Bons und entlastetes Personal – wenn die Lösung zum Konzept passt. Dieser Leitfaden ordnet die Möglichkeiten und zeigt, worauf es bei Auswahl und Einführung ankommt.',
    sections: [
      section(
        'Was „Smart Order“ bedeutet',
        p('Unter smarten Order-Lösungen fassen wir alle digitalen Wege zusammen, auf denen Gäste selbst bestellen oder auf denen Bestellungen ohne Umweg in die Küche gelangen. Sie ergänzen die klassische Kasse – ersetzen sie aber nicht.'),
        list([
          '**QR-Code-Bestellung am Tisch:** Gast scannt, bestellt und bezahlt über das eigene Smartphone.',
          '**Self-Order-Kiosk:** Selbstbestellterminal im Eingangs- oder Thekenbereich, meist mit Kartenzahlung.',
          '**Digitale Speisekarte:** Karte auf dem Smartphone – mit Fotos, Allergenen und Tagesaktualität, optional mit Bestellfunktion.',
          '**Pre-Order & Pick-up:** Vorbestellung über Website oder App zur Abholung oder zum Verzehr vor Ort.',
          '**Küchendisplay (KDS):** Bestellungen erscheinen digital in der Küche statt auf Bonrollen.',
          '**Handheld beim Service:** Die Servicekraft bestellt am Tisch direkt ins System (siehe Bereich Kassensysteme).',
        ])
      ),
      section(
        'Die Vorteile im Alltag',
        p('Richtig eingesetzt wirken Order-Lösungen an mehreren Stellen gleichzeitig:'),
        list([
          '**Höhere Durchschnittsbons:** Gäste bestellen bei digitaler Auswahl häufiger Extras, Beilagen und ein zweites Getränk – Zuwächse von 10 bis 25 Prozent sind branchenüblich, wenn Upselling sinnvoll integriert ist.',
          '**Entlastung des Personals:** Bestellaufnahme und Kassieren entfallen teilweise; das Team konzentriert sich auf Servieren und Gastkontakt.',
          '**Weniger Fehler:** Der Gast wählt selbst – keine Missverständnisse bei Varianten und Sonderwünschen.',
          '**Kürzere Wartezeiten:** Nachbestellungen gehen sofort in die Küche, ohne auf die Servicekraft zu warten.',
          '**Bessere Daten:** Jede Bestellung ist Artikel-, Zeit- und Tischgenau erfasst.',
          '**Mehrsprachigkeit** ohne Zusatzaufwand für das Team.',
        ])
      ),
      section(
        'Welche Lösung für welches Konzept?',
        table(
          ['Konzept', 'Passende Lösung', 'Begründung'],
          [
            ['Schnellrestaurant, Imbiss, Systemgastro', 'Self-Order-Kiosk + KDS', 'Hohe Frequenz, standardisierte Produkte, Schlangen vermeiden'],
            ['Biergarten, Terrasse, Selbstbedienung', 'QR-Bestellung mit Bezahlung', 'Große Flächen, wenig Personal pro Gast'],
            ['Café, Bäckerei', 'Pre-Order & Pick-up, Kiosk in Stoßzeiten', 'Pendler bestellen vorab, Theke wird entlastet'],
            ['À-la-carte-Restaurant', 'Digitale Speisekarte + Handheld, QR für Nachbestellung', 'Gastkontakt bleibt zentral, Effizienz im Hintergrund'],
            ['Bar, Club, Event', 'QR-Bestellung mit Bezahlung, Abholstationen', 'Laute Umgebung, hohe Getränkefrequenz'],
            ['Hotel-Gastronomie', 'QR für Zimmerservice und Poolbar, digitale Karte', 'Dezentrale Bestellpunkte, mehrsprachige Gäste'],
          ]
        )
      ),
      section(
        'Gastfreundschaft und Automatisierung – kein Widerspruch',
        p('Die häufigste Sorge: „Meine Gäste wollen mit Menschen sprechen, nicht mit einem QR-Code.“ Das ist berechtigt – und genau deshalb funktionieren **Hybridmodelle** am besten. Die Begrüßung, Empfehlung und der erste Kontakt bleiben persönlich; die zweite Getränkerunde bestellt der Gast per QR-Code, wenn er nicht auf die Servicekraft warten möchte. Digitale Bestellung ersetzt keine Gastfreundschaft, sie schafft Zeit dafür.'),
        tip('Gib Gästen immer die Wahl. Ein Hinweis wie „Bestellen Sie bequem per QR-Code – oder sprechen Sie uns jederzeit an“ nimmt den Druck raus und erhöht die Akzeptanz spürbar.')
      ),
      section(
        'Integration in Kasse und Küche',
        p('Der wichtigste technische Punkt: Alle Bestellkanäle müssen in **dieselbe Kasse** und denselben Artikelstamm laufen. Sonst pflegst du Preise und Artikel mehrfach, und die Umsätze passen am Tagesende nicht zusammen.'),
        list([
          'Ein Artikelstamm für Kasse, Kiosk, QR-Karte und Webshop',
          'Bestellungen aller Kanäle erscheinen im selben Küchensystem (Drucker oder KDS) – markiert nach Herkunft',
          'Zahlungen aus QR- und Kiosk-Bestellungen fließen in den Tagesabschluss der Kasse',
          'Tischzuordnung bei QR-Bestellungen, damit der Service weiß, wohin die Bestellung gehört',
          'Artikel „ausverkauft“ einmal setzen – überall ausgeblendet',
        ])
      ),
      section(
        'Kosten und Wirtschaftlichkeit',
        table(
          ['Lösung', 'Einmalig', 'Laufend', 'Typischer Effekt'],
          [
            ['QR-Bestellung', 'ca. 0 – 500 € (Aufsteller, Einrichtung)', 'ca. 30 – 150 € / Monat oder Anteil am Bestellwert', 'Höhere Bons, weniger Laufwege'],
            ['Self-Order-Kiosk', 'ca. 1.500 – 5.000 € pro Terminal', 'ca. 50 – 150 € / Monat pro Terminal', 'Bon +15 – 30 %, kürzere Schlangen'],
            ['Digitale Speisekarte', 'gering', 'ca. 10 – 50 € / Monat', 'Mehrsprachigkeit, aktuelle Preise, Vorstufe zur Bestellung'],
            ['Küchendisplay', 'ca. 500 – 1.500 € pro Station', 'meist in Kassenlizenz enthalten oder 20 – 50 € / Monat', 'Schnellere, fehlerfreie Küche'],
          ]
        ),
        p('Rechenbeispiel: Ein Biergarten mit 300 Gästen an einem Sommerabend und 22 € Durchschnittsbon erzielt 6.600 € Umsatz. Steigt der Bon durch QR-Bestellung um 12 % auf knapp 25 €, sind das rund 800 € Mehrumsatz pro Abend – die monatlichen Kosten der Lösung amortisieren sich am ersten Wochenende.')
      ),
      section(
        'Checkliste für die Auswahl',
        steps([
          '**Kassenanbindung prüfen:** Echte Integration in deine bestehende Kasse oder separater Artikelstamm?',
          '**Bestellfluss testen:** Wie viele Klicks bis zur Bestellung? Funktioniert es ohne App-Download und ohne Registrierung?',
          '**Bezahlung:** Welche Zahlarten? Trinkgeldabfrage? Rechnung teilen?',
          '**Gestaltung:** Kannst du Fotos, Beschreibungen, Allergene und dein Branding selbst pflegen?',
          '**Upselling-Logik:** Lassen sich Empfehlungen und Kombis konfigurieren?',
          '**Betriebsmodell:** Nur Karte anzeigen, Bestellen ohne Bezahlen (Zahlung beim Service) oder Bestellen mit Bezahlen?',
          '**Kostenmodell:** Feste Gebühr oder Prozent vom Bestellwert – auf deine Umsätze hochrechnen.',
        ])
      ),
      section(
        'Einführung und Kommunikation an die Gäste',
        p('Starte mit einem Bereich oder einer Tageszeit – etwa der Terrasse oder dem Mittagsgeschäft – und weite nach zwei bis drei Wochen aus. Bereite das Team vor: Wer hilft Gästen beim ersten Scan? Wie erkennt der Service, dass ein Tisch bereits digital bestellt hat? Wie läuft die Rechnung, wenn ein Teil digital und ein Teil beim Service bestellt wurde?'),
        p('Für Gäste zählen drei Dinge: ein **gut sichtbarer QR-Code** (Tischaufsteller, Untersetzer, Karte), ein **einfacher Ablauf ohne Registrierung** und der Hinweis, dass das Team weiterhin jederzeit ansprechbar ist.')
      ),
      section(
        'Was in der Praxis häufig schiefgeht',
        list([
          '**Schlechtes WLAN oder Mobilfunk:** Wenn der Scan nicht lädt, ist der Gast verloren. Prüfe Empfang an jedem Tisch und biete Gäste-WLAN an.',
          '**Karte ohne Bilder und Beschreibungen:** Digitale Karten leben von Fotos – ohne sie sinkt der Bon statt zu steigen.',
          '**Pflichtregistrierung:** Jede Hürde vor der ersten Bestellung kostet Umsatz.',
          '**Team nicht eingebunden:** Servicekräfte, die digitale Bestellungen als Konkurrenz empfinden, blockieren die Einführung.',
          '**Kanäle nicht integriert:** Zwei Artikelstämme, zwei Tagesabschlüsse, doppelte Arbeit.',
        ])
      ),
      section(
        'Fragen aus der Praxis',
        faq([
          { q: 'Nehmen QR-Bestellung und Kiosk meinem Team Trinkgeld weg?', a: 'Nicht zwangsläufig. Bei digitaler Bezahlung kann eine Trinkgeldabfrage integriert werden – und die Erfahrung zeigt, dass Gäste bei guter Servicequalität digital ähnlich oder sogar mehr Trinkgeld geben. Wichtig ist eine transparente Verteilung im Team.' },
          { q: 'Muss der Gast eine App installieren?', a: 'Nein. Gute Lösungen funktionieren direkt im Browser des Smartphones nach dem Scan. Eine App-Pflicht wäre eine erhebliche Hürde und ist nicht zu empfehlen.' },
          { q: 'Was passiert, wenn ein Gast digital bestellt und dann nicht bezahlt?', a: 'Beim Modell „Bestellen mit Bezahlen“ ist die Zahlung vor der Zubereitung abgeschlossen – das Risiko entfällt. Beim Modell „Bestellen ohne Bezahlen“ gilt derselbe Prozess wie bei einer klassischen Bestellung beim Service.' },
          { q: 'Passt digitale Bestellung zu gehobener Gastronomie?', a: 'Dort steht der persönliche Service im Vordergrund. Sinnvoll sind hier eher digitale Speisekarten mit Allergenen und Mehrsprachigkeit sowie Handhelds für den Service – die Bestellaufnahme bleibt persönlich.' },
        ])
      ),
    ],
  },
  subArticles: [
    {
      slug: 'qr-code-bestellung-am-tisch',
      title: 'QR-Code-Bestellung am Tisch: Konzepte, Vorteile und Stolperfallen',
      shortTitle: 'QR-Bestellung am Tisch',
      teaser: 'Nur Karte anzeigen, bestellen oder gleich bezahlen? Welche QR-Modelle es gibt, was du technisch brauchst und wie du die Akzeptanz bei Gästen und Team sicherst.',
      intro: 'Der QR-Code auf dem Tisch ist der einfachste Einstieg in digitale Bestellung: keine Hardware, kein App-Download, sofort einsatzbereit. Ob er Umsatz bringt oder Gäste nervt, entscheidet die Umsetzung.',
      sections: [
        section(
          'Drei Betriebsmodelle im Vergleich',
          table(
            ['Modell', 'Ablauf', 'Geeignet für'],
            [
              ['Nur Speisekarte', 'Gast scannt, sieht Karte, bestellt beim Service', 'Gehobene Gastronomie, Einstieg in digitale Karte'],
              ['Bestellen ohne Bezahlen', 'Gast bestellt digital, zahlt am Ende beim Service', 'À la carte mit Nachbestellungen, gemischte Tische'],
              ['Bestellen mit Bezahlen', 'Gast bestellt und zahlt sofort, Service bringt nur', 'Biergarten, Bar, Selbstbedienung, hohe Frequenz'],
            ]
          ),
          p('Viele Betriebe kombinieren: Die erste Bestellung nimmt der Service auf, Nachbestellungen laufen per QR. So bleibt der Gastkontakt erhalten, und niemand wartet auf das zweite Getränk.')
        ),
        section(
          'Technische Voraussetzungen',
          list([
            '**Stabiler Empfang** an jedem Tisch – Gäste-WLAN mit einfachem Zugang oder guter Mobilfunk',
            '**Tischnummern** im System, damit Bestellungen zugeordnet werden können',
            '**Kassenanbindung**, damit Bestellungen und Zahlungen im Tagesabschluss landen',
            '**Küchenrouting** für QR-Bestellungen – idealerweise mit Kennzeichnung der Herkunft',
            '**Zahlungsanbindung** mit gängigen Karten und Wallet-Zahlung, optional Trinkgeldabfrage',
            '**Aufsteller oder Aufkleber** mit QR-Code je Tisch – wetterfest im Außenbereich',
          ])
        ),
        section(
          'Die digitale Karte gestalten',
          p('Eine digitale Karte ist kein abfotografiertes PDF. Sie sollte mobil optimiert sein, mit Kategorien und Suchfunktion, appetitlichen Fotos, kurzen Beschreibungen, Allergen- und Zusatzstoffangaben und klaren Preisen. Empfehlungen („Passt dazu“) und Kombiangebote steigern den Bon – ohne aufdringlich zu wirken.'),
          tip('Die ersten drei Artikel jeder Kategorie werden am häufigsten bestellt. Platziere dort Gerichte mit gutem Deckungsbeitrag, nicht nur die günstigsten.')
        ),
        section(
          'Akzeptanz bei Gästen und Team',
          p('Gäste akzeptieren QR-Bestellung, wenn sie einfach ist und als Service verstanden wird – nicht als Personalersatz. Das Team akzeptiert sie, wenn klar ist, dass Trinkgeld weiterhin ankommt und die Arbeit leichter wird, nicht weniger wertgeschätzt. Sprich beides offen an.'),
          list([
            'Kurzer Hinweistext am Tisch: „Bestellen per QR – oder wir kommen gern zu Ihnen“',
            'Service begrüßt jeden Tisch persönlich und erklärt bei Bedarf den Ablauf',
            'Trinkgeldabfrage bei digitaler Zahlung aktivieren und transparent verteilen',
            'Service sieht digitale Bestellungen sofort und bringt sie zügig – sonst verliert das Modell Vertrauen',
          ])
        ),
        section(
          'Häufige Stolperfallen',
          list([
            'QR-Code führt auf eine langsame oder nicht mobil optimierte Seite',
            'Registrierungspflicht oder App-Download vor der ersten Bestellung',
            'Artikel in der QR-Karte weichen von der Kasse ab (Preise, Verfügbarkeit)',
            'Service wird nicht informiert, wenn ein Tisch digital bestellt hat',
            'Kein Prozess für gemischte Rechnungen (teils digital, teils beim Service)',
          ])
        ),
      ],
    },
    {
      slug: 'self-order-kiosk',
      title: 'Self-Order-Kiosk: Für welche Betriebe sich Selbstbestellterminals lohnen',
      shortTitle: 'Self-Order-Kiosk',
      teaser: 'Kürzere Schlangen, höhere Bons, entlastete Theke: Was ein Selbstbestellterminal leistet, was es kostet und wie du Menüführung und Standort optimal gestaltest.',
      intro: 'Selbstbestellterminals kennt jeder aus der Systemgastronomie. Inzwischen sind sie auch für Einzelbetriebe erschwinglich – und in den richtigen Konzepten einer der stärksten Umsatzhebel überhaupt.',
      sections: [
        section(
          'Für wen sich ein Kiosk lohnt',
          p('Kioske funktionieren dort, wo Gäste an einer Theke bestellen und in Stoßzeiten Schlangen entstehen: Schnellrestaurants, Imbisse, Burger- und Pizzakonzepte, Bäckereien mit Snackangebot, Food-Courts, Kantinen und Kinos. Weniger geeignet sind sie für Konzepte mit Tischservice, bei denen die Bestellung Teil des Gastkontakts ist.'),
          list([
            'Regelmäßige Warteschlangen an der Theke in Stoßzeiten',
            'Standardisiertes Angebot mit klaren Varianten und Extras',
            'Personalmangel an der Kasse, während die Küche Kapazität hätte',
            'Hoher Anteil an Kartenzahlung',
          ])
        ),
        section(
          'Die messbaren Effekte',
          p('Am Kiosk bestellen Gäste in Ruhe, ohne den Druck der Schlange hinter sich – und wählen dabei häufiger Extras, Größen-Upgrades und Menüs. Bon-Steigerungen von **15 bis 30 Prozent** sind bei gut gestalteter Menüführung realistisch. Gleichzeitig verkürzen sich Wartezeiten, weil mehrere Gäste parallel bestellen, während die Theke nur noch ausgibt.')
        ),
        section(
          'Hardware und Standort',
          table(
            ['Bauform', 'Vorteile', 'Zu beachten'],
            [
              ['Standterminal (freistehend)', 'Prominent, barrierefrei nutzbar, große Displays', 'Platzbedarf, höherer Preis'],
              ['Wandmontage', 'Platzsparend', 'Höhe für alle Gäste geeignet wählen'],
              ['Tablet auf Ständer', 'Günstig, flexibel', 'Weniger robust, kleineres Display, Kartenleser separat'],
            ]
          ),
          p('Platziere Kioske dort, wo Gäste ankommen – im Eingangsbereich, nicht neben der Theke. Rechne mit einem Terminal pro 50 – 80 Gäste in der Spitzenstunde. Ein integrierter Kartenleser und ein Bondrucker mit Abholnummer gehören zur Grundausstattung; die Anzeige der Bestellnummer auf einem Bildschirm an der Ausgabe rundet den Ablauf ab.')
        ),
        section(
          'Menüführung, die verkauft',
          list([
            'Startbildschirm mit den beliebtesten Produkten und aktuellen Aktionen',
            'Maximal drei Ebenen bis zur Bestellung',
            'Große Fotos, kurze Texte, klare Preise',
            'Gezielte Zusatzfragen: „Als Menü?“, „Größe wählen“, „Dazu ein Dessert?“ – maximal zwei pro Bestellung',
            'Allergenfilter und Mehrsprachigkeit',
            'Bestellabschluss in unter 90 Sekunden',
          ]),
          tip('Werte nach vier Wochen aus, welche Zusatzfragen angenommen werden und welche Gäste abbrechen lassen. Eine einzige gut platzierte Frage nach dem Getränk bringt oft mehr als fünf Upselling-Pop-ups.')
        ),
        section(
          'Kosten und Rolle des Personals',
          p('Rechne mit 1.500 – 5.000 € pro Terminal plus 50 – 150 € Software im Monat. Das Personal fällt nicht weg, sondern wechselt die Rolle: von der Bestellaufnahme zur Ausgabe, zur Hilfe am Terminal und zur Gastbetreuung im Raum. Kommuniziere das früh und deutlich im Team.')
        ),
      ],
    },
    {
      slug: 'digitale-speisekarte',
      title: 'Digitale Speisekarte: Mehr als ein PDF auf dem Smartphone',
      shortTitle: 'Digitale Speisekarte',
      teaser: 'Warum eine echte digitale Karte kein abfotografiertes PDF ist, welche Pflichtangaben sie braucht und wie sie mit der Kasse verbunden wird.',
      intro: 'Viele Betriebe haben in den letzten Jahren einen QR-Code auf den Tisch geklebt, der zu einem PDF führt. Das ist kein digitales Menü, sondern eine digitale Kopie eines analogen Problems. Eine echte digitale Speisekarte kann deutlich mehr.',
      sections: [
        section(
          'Warum ein PDF nicht reicht',
          list([
            'Auf dem Smartphone muss gezoomt und geschoben werden – Gäste geben auf',
            'Preisänderungen erfordern neues Layout, neuen Export, neuen Upload',
            'Keine Fotos, keine Filter, keine Suchfunktion, keine Sprachen',
            'Tagesgerichte und ausverkaufte Artikel sind nicht aktuell',
            'Keine Verbindung zur Kasse – Preise laufen auseinander',
          ])
        ),
        section(
          'Was eine gute digitale Karte leistet',
          list([
            '**Mobil optimiert** mit Kategorien, Suche und Sprungmarken',
            '**Fotos und Beschreibungen**, die Appetit machen und Fragen vorwegnehmen',
            '**Allergene und Zusatzstoffe** direkt am Gericht, optional mit Filter („ohne Gluten“)',
            '**Mehrsprachigkeit** per Klick – automatisch übersetzt und redaktionell geprüft',
            '**Tagesaktualität:** Tagesgerichte, Verfügbarkeit und Preise zentral in Sekunden ändern',
            '**Anbindung an die Kasse:** ein Artikelstamm für Karte, Kasse und Bestellkanäle',
            '**Erweiterbar** um Bestell- und Bezahlfunktion, wenn du später möchtest',
          ])
        ),
        section(
          'Rechtliche Pflichtangaben',
          p('Auch digital gelten die Vorgaben der Preisangabenverordnung und der Lebensmittelinformations-Verordnung: Preise inklusive Mehrwertsteuer, Angabe von Allergenen und kennzeichnungspflichtigen Zusatzstoffen (Konservierungsstoffe, Farbstoffe, Geschmacksverstärker usw.), bei Getränken Füllmengen. Ein Vorteil der digitalen Karte: Änderungen an Rezepturen lassen sich sofort nachpflegen – ohne Karten neu zu drucken.'),
          warn('Halte weiterhin eine gedruckte Karte oder eine Tafel bereit. Nicht jeder Gast hat ein Smartphone dabei oder möchte es nutzen – und bei Empfangsproblemen darf die Bestellung nicht scheitern.')
        ),
        section(
          'Gestaltungstipps aus der Praxis',
          list([
            'Weniger ist mehr: 6 – 8 Gerichte pro Kategorie sind übersichtlicher als 20',
            'Ein einheitlicher Bildstil – lieber keine Fotos als schlechte',
            'Beschreibungen mit maximal zwei Zeilen, Herkunft und Zubereitung statt Adjektiv-Ketten',
            'Empfehlungen und Kombis („Passt dazu“) sparsam einsetzen',
            'Preise ohne Währungssymbol und ohne Punkt-Linien wirken weniger „preisgetrieben“',
          ]),
          tip('Nutze die digitale Karte als Testfeld: Ändere Reihenfolge oder Beschreibung eines Gerichts und beobachte in der Kasse, ob sich der Absatz verändert. Solche Tests sind gedruckt unmöglich.')
        ),
      ],
    },
    {
      slug: 'kuechendisplay-kds',
      title: 'Küchendisplay statt Bonrolle: Wie ein KDS die Küche entlastet',
      shortTitle: 'Küchendisplay (KDS)',
      teaser: 'Gangsteuerung, Timer, Stationen und Statistiken: Was ein Kitchen Display System kann, für welche Küchen es sich lohnt und wie du es einführst.',
      intro: 'Bonrollen, die im Fettdunst kleben, verloren gehen oder in falscher Reihenfolge abgearbeitet werden – die Küche ist oft der letzte analoge Ort im Betrieb. Ein Küchendisplay bringt Ordnung, Tempo und messbare Zeiten.',
      sections: [
        section(
          'Was ein KDS ist',
          p('Ein Kitchen Display System zeigt Bestellungen auf einem Bildschirm in der Küche an – sortiert nach Eingang, Station oder Gang. Köche „bumpen“ fertige Positionen per Touch oder Taster, der Service sieht den Status, und das System misst automatisch, wie lange jede Bestellung gedauert hat. Es ersetzt den Bondrucker oder ergänzt ihn für einzelne Stationen.')
        ),
        section(
          'Vorteile gegenüber dem Bondrucker',
          list([
            '**Übersicht:** Alle offenen Bestellungen auf einen Blick, farblich nach Wartezeit markiert',
            '**Gangsteuerung:** Hauptgang wird erst angezeigt, wenn der Service ihn abruft oder die Vorspeise „gebumpt“ ist',
            '**Stationsrouting:** Kalte Küche, Grill, Beilagen sehen nur ihre Positionen – und wann alles zusammen raus muss',
            '**Änderungen in Echtzeit:** Stornos und Ergänzungen erscheinen sofort, kein Bon-Suchen',
            '**Kennzahlen:** Zubereitungszeit pro Gericht, Station und Tageszeit – Grundlage für Prozess- und Kartenoptimierung',
            '**Kosten und Nachhaltigkeit:** Keine Bonrollen, kein Druckerausfall im Service',
          ])
        ),
        section(
          'Für welche Küchen es sich lohnt',
          p('Je mehr Stationen, Gänge und Bestellkanäle zusammenlaufen, desto größer der Nutzen. Besonders profitieren Küchen mit Lieferbestellungen aus mehreren Plattformen, Konzepte mit Kiosk- oder QR-Bestellung sowie Betriebe mit getrennten Stationen. In einer kleinen Küche mit zwei Personen und 40 Plätzen reicht oft weiterhin ein Drucker – hier lohnt ein KDS vor allem als Ausgabe-Bildschirm für den Service.')
        ),
        section(
          'Hardware und Einrichtung',
          list([
            'Hitzefeste, spritzwassergeschützte Displays (15 – 22 Zoll) oder robuste Tablets in Schutzgehäusen',
            'Bedienung per Touch oder externem Taster („Bump Bar“) für fettige Hände',
            'Stabile Netzwerkanbindung – kabelgebunden bevorzugt',
            'Anbindung an die Kasse: alle Kanäle (Service, QR, Kiosk, Delivery) müssen auf dem Display erscheinen',
            'Optional: Kundenmonitor an der Ausgabe mit Bestellnummern',
          ]),
          tip('Definiere pro Gericht eine Zielzeit und lass das Display bei Überschreitung die Farbe wechseln. Allein diese Sichtbarkeit verkürzt Wartezeiten spürbar – ohne dass jemand schneller arbeiten muss.')
        ),
        section(
          'Einführung in der Küche',
          p('Küchenteams sind Gewohnheitstiere – zu Recht, denn Routine ist im Stress überlebenswichtig. Führe das KDS an ruhigen Tagen ein, lass den Drucker in der ersten Woche parallel laufen und passe Anzeige-Logik (Sortierung, Schriftgröße, Farben) gemeinsam mit dem Küchenchef an. Nach zwei Wochen sollte der Drucker abgeschaltet werden, damit sich niemand auf das alte System zurückzieht.')
        ),
      ],
    },
  ],
};
